import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Tooltip } from 'components/ui/tooltip';
import { ReactElement, ReactNode } from 'react';
import { cn } from 'utils/cn';

/** The wide, near-white card both modes draw, shifted clear of the trigger's left edge. */
const BUBBLE =
    'bg-grey-0 text-grey-700 ml-[100px] w-[400px] max-w-[400px] rounded-[7px] p-0 text-[11px] new:text-caption shadow-md';

interface LightTooltipProps {
    title: ReactNode;
    children: ReactElement;
    /** `hover` reveals the card on hover; `click` opens it as a popover the pointer can operate. */
    mode?: 'click' | 'hover';
    /**
     * Names the `click` mode's trigger, which is a button: needed when `children` can be a glyph alone. It
     * starts with any text the trigger shows.
     */
    label?: string;
    onOpen?: () => void;
    onClose?: () => void;
}

export const LightTooltip = ({ mode = 'hover', children, title, label, onOpen, onClose }: LightTooltipProps) => {
    const handleOpenChange = (open: boolean) => {
        if (open) {
            onOpen?.();
        } else {
            onClose?.();
        }
    };

    if (mode === 'click') {
        // A popover, not a tooltip: it owns the outside-press, the Esc key and the focus return, which
        // a tooltip has none of and which this mode used to hand-roll with a click-away listener.
        return (
            <Popover onOpenChange={handleOpenChange}>
                <PopoverTrigger nativeButton={false} render={<span />} aria-label={label} className="cursor-pointer">
                    {children}
                </PopoverTrigger>
                {/* On the tooltip tier: the popover tier sits below the nav and any drawer. Only above or
                    below the trigger, capped to the room there so a tall card scrolls: the copy icon sits
                    right of the trigger, where a card on that side would cover it. */}
                <PopoverContent
                    className={cn(BUBBLE, 'max-h-(--available-height) overflow-y-auto')}
                    positionerClassName="z-(--z-tooltip)"
                    side="bottom"
                    collisionAvoidance={{ fallbackAxisSide: 'none' }}
                >
                    {title}
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <Tooltip content={title} className={BUBBLE} side="bottom" onOpenChange={handleOpenChange} disableHoverablePopup>
            {children}
        </Tooltip>
    );
};
